import { defineMessages } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { GALERIA_TITULO, GRADE_COLUNAS_PADRAO } from './helpers';

const messages = defineMessages({
  titleBlock: {
    id: 'mpf_galeria_imagens_block',
    defaultMessage: 'Galeria de imagens',
  },
  title: {
    id: 'mpf_galeria_imagens_title',
    defaultMessage: 'Título do cartão',
  },
  titleHelp: {
    id: 'mpf_galeria_imagens_title_help',
    defaultMessage: 'Em branco, usa "Galeria de imagens".',
  },
  items: { id: 'mpf_galeria_imagens_items', defaultMessage: 'Fotos' },
  itemsHelp: {
    id: 'mpf_galeria_imagens_items_help',
    defaultMessage:
      'As fotos aparecem na ordem desta lista. Item sem imagem não entra na galeria.',
  },
  item: { id: 'mpf_galeria_imagens_item', defaultMessage: 'Foto' },
  image: { id: 'mpf_galeria_imagens_image', defaultMessage: 'Imagem' },
  imageHelp: {
    id: 'mpf_galeria_imagens_image_help',
    defaultMessage:
      'Arraste um arquivo, escolha uma imagem do site ou cole uma URL.',
  },
  alt: { id: 'mpf_galeria_imagens_alt', defaultMessage: 'Texto alternativo' },
  altHelp: {
    id: 'mpf_galeria_imagens_alt_help',
    defaultMessage: 'Descreva o que a foto mostra, para leitores de tela.',
  },
  caption: { id: 'mpf_galeria_imagens_caption', defaultMessage: 'Legenda' },
  captionHelp: {
    id: 'mpf_galeria_imagens_caption_help',
    defaultMessage: 'Texto sobre a foto no palco e no modo ampliado.',
  },
  credito: { id: 'mpf_galeria_imagens_credito', defaultMessage: 'Crédito' },
  creditoHelp: {
    id: 'mpf_galeria_imagens_credito_help',
    defaultMessage:
      'Só para esta foto. Em branco, usa o crédito padrão da galeria.',
  },
  creditoPadrao: {
    id: 'mpf_galeria_imagens_credito_padrao',
    defaultMessage: 'Crédito padrão',
  },
  creditoPadraoHelp: {
    id: 'mpf_galeria_imagens_credito_padrao_help',
    defaultMessage:
      'Autoria que vale para todas as fotos (ex.: "Foto: Nome/MPF"). Uma foto com crédito próprio usa o dela.',
  },
  colunasGrade: {
    id: 'mpf_galeria_imagens_colunas',
    defaultMessage: 'Colunas da grade',
  },
  colunasGradeHelp: {
    id: 'mpf_galeria_imagens_colunas_help',
    defaultMessage:
      'Quantas fotos por linha em "Ver todas", no desktop. No celular são sempre duas.',
  },
  mostrarCredito: {
    id: 'mpf_galeria_imagens_mostrar_credito',
    defaultMessage: 'Exibir os créditos',
  },
  mostrarCreditoHelp: {
    id: 'mpf_galeria_imagens_mostrar_credito_help',
    defaultMessage: 'Desligado, esconde a linha de crédito de todas as fotos.',
  },
});

/** Campos de uma foto da galeria. */
const itemSchema = ({ intl }: { intl: IntlShape }) => ({
  title: intl.formatMessage(messages.item),
  addMessage: intl.formatMessage(messages.item),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['image', 'alt', 'caption', 'credito'],
    },
  ],
  properties: {
    // `image` (e não `object_browser`): este widget navega no site, aceita
    // arrastar um arquivo para subir e também colar uma URL. É a decisão que
    // os outros blocos de imagem curada do projeto já tomaram — ver o
    // comentário de `resolveCuratedImage` sobre por que congelar as URLs de
    // escala de uma referência quebra o card quando a redação troca a foto.
    image: {
      title: intl.formatMessage(messages.image),
      description: intl.formatMessage(messages.imageHelp),
      widget: 'image',
    },
    alt: {
      title: intl.formatMessage(messages.alt),
      description: intl.formatMessage(messages.altHelp),
    },
    caption: {
      title: intl.formatMessage(messages.caption),
      description: intl.formatMessage(messages.captionHelp),
    },
    // Sobrescrita: vale só para esta foto. O padrão da galeria fica no nível
    // do bloco, para uma cobertura inteira do mesmo fotógrafo ser preenchida
    // uma vez só.
    credito: {
      title: intl.formatMessage(messages.credito),
      description: intl.formatMessage(messages.creditoHelp),
    },
  },
  required: ['image', 'alt'],
});

/**
 * Schema do bloco "Galeria de imagens".
 *
 * `activeObject`/`setActiveObject` mantêm o `ObjectListWidget` no modo
 * controlado: `-1` (e não `undefined`) é o que o deixa com tudo fechado — ver
 * `Blocks/SubHomeGaleria/schema.ts`.
 */
export const mpfGaleriaImagensSchema = ({
  intl,
  activeObject,
  setActiveObject,
}: {
  intl: IntlShape;
  activeObject?: number;
  setActiveObject?: (index: number) => void;
}) => ({
  title: intl.formatMessage(messages.titleBlock),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: [
        'title',
        'items',
        'creditoPadrao',
        'colunasGrade',
        'mostrarCredito',
      ],
    },
  ],
  properties: {
    title: {
      title: intl.formatMessage(messages.title),
      description: intl.formatMessage(messages.titleHelp),
      placeholder: GALERIA_TITULO,
    },
    items: {
      title: intl.formatMessage(messages.items),
      description: intl.formatMessage(messages.itemsHelp),
      widget: 'object_list',
      schema: itemSchema({ intl }),
      activeObject,
      setActiveObject,
    },
    creditoPadrao: {
      title: intl.formatMessage(messages.creditoPadrao),
      description: intl.formatMessage(messages.creditoPadraoHelp),
    },
    colunasGrade: {
      title: intl.formatMessage(messages.colunasGrade),
      description: intl.formatMessage(messages.colunasGradeHelp),
      // Números, não strings: o View usa o valor como chave de uma tabela de
      // classes do Tailwind, e '4' !== 4 cairia no padrão em silêncio.
      choices: [
        [2, '2'],
        [3, '3'],
        [4, '4'],
      ],
      default: GRADE_COLUNAS_PADRAO,
    },
    mostrarCredito: {
      title: intl.formatMessage(messages.mostrarCredito),
      description: intl.formatMessage(messages.mostrarCreditoHelp),
      type: 'boolean',
      default: true,
    },
  },
  required: [],
});

export default mpfGaleriaImagensSchema;
